"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import useSWR from "swr";
import { apiClient } from "@/lib/api-client";
import type { DocTheme } from "./types";
import { resolveTheme, sanitizeTheme } from "./compute";
import { DEFAULT_PRESET_ID } from "./presets";

const fetcher = <T,>(url: string): Promise<T> =>
  apiClient.get<T>(url).then((r) => r.data);

interface UseDocThemeResult {
  /** Effective theme to render (perDoc ?? userDefault ?? default). */
  theme: DocTheme;
  /** The doc's own override, if any (null = inheriting). */
  docTheme: DocTheme | null;
  /** The user's default, if any. */
  userDefault: DocTheme | null;
  loading: boolean;
  /** True when the doc has its own override (not inheriting the user default). */
  isOverriding: boolean;
  /** Set/replace the per-doc theme (optimistic; debounced persist). */
  setDocTheme: (theme: DocTheme) => void;
  /** Clear the per-doc override → revert to the user default. */
  clearDocTheme: () => void;
  /** Persist the given (or current effective) theme as the user's default. */
  saveAsUserDefault: (theme?: DocTheme) => Promise<void>;
}

/**
 * Loads + resolves a document's theme and exposes mutators that persist to the
 * doc (metadata.theme) and to the user's default (/api/me/settings). The
 * effective `theme` updates optimistically so the editor restyles instantly.
 */
export function useDocTheme(
  documentId: string,
  docMetadata?: string | null,
): UseDocThemeResult {
  // Per-doc override parsed from the document's metadata JSON.
  const parsedDocTheme = parseDocTheme(docMetadata);
  const [docTheme, setDocThemeState] = useState<DocTheme | null>(parsedDocTheme);

  // Keep local state in sync if the document metadata reloads.
  useEffect(() => {
    setDocThemeState(parseDocTheme(docMetadata));
  }, [docMetadata]);

  // User default from settings.
  const { data: settings, isLoading } = useSWR<{ docTheme?: DocTheme }>(
    "/api/me/settings",
    fetcher,
  );
  const userDefault = settings?.docTheme
    ? sanitizeTheme(settings.docTheme)
    : null;

  const theme = resolveTheme(docTheme, userDefault);

  // Debounced persist of the per-doc theme.
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const persistDocTheme = useCallback(
    (next: DocTheme | null) => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => {
        apiClient
          .patch(`/api/documents/${documentId}`, { theme: next })
          .catch(() => {});
      }, 800);
    },
    [documentId],
  );

  useEffect(
    () => () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    },
    [],
  );

  const setDocTheme = useCallback(
    (next: DocTheme) => {
      setDocThemeState(next);
      persistDocTheme(next);
    },
    [persistDocTheme],
  );

  const clearDocTheme = useCallback(() => {
    setDocThemeState(null);
    persistDocTheme(null);
  }, [persistDocTheme]);

  const saveAsUserDefault = useCallback(
    async (t?: DocTheme) => {
      const toSave = t ?? theme;
      await apiClient.patch("/api/me/settings", { docTheme: toSave });
    },
    [theme],
  );

  return {
    theme,
    docTheme,
    userDefault,
    loading: isLoading,
    isOverriding: docTheme !== null,
    setDocTheme,
    clearDocTheme,
    saveAsUserDefault,
  };
}

function parseDocTheme(metadata?: string | null): DocTheme | null {
  if (!metadata) return null;
  try {
    const meta = JSON.parse(metadata) as { theme?: unknown };
    if (!meta.theme) return null;
    return sanitizeTheme(meta.theme);
  } catch {
    return null;
  }
}

export { DEFAULT_PRESET_ID };
